import { useState } from 'preact/hooks';
import { useTranslation } from 'react-i18next';
import { isNoInfoUpdate, hasReturnTickets } from '@pperform/pp-utils/parcelOrg';

import { usePTPConfigContext } from 'GlobalConfig.context';
import { useFeatureToggleContext } from 'FeatureConfig.context';
import type Shipment from 'interfaces/PmParcel';

import Button from 'components/Button';
import { Stack } from 'components/Stack';
import RetrySearchForm from 'components/RetrySearchForm';
import { TrackingDetailsHeader } from 'components/MerchantTrackingWidget/components/TrackingDetailsHeader';
import { CarrierInformation } from 'components/MerchantTrackingWidget/components/CarrierInformation';
import { TrackingTimeline } from 'components/MerchantTrackingWidget/components/TrackingTimeline';
import { ProgressIndicator } from '../ProgressIndicator';
import { ReturnScreen } from '../ReturnScreen';

import {
  Body,
  SearchFormBox,
  TrackingDetailsContainer,
  ProgressIndicatorContainer,
} from './TrackingDetails.styles';
import { isShipmentReturnable } from './TrackingDetails.services';

interface TrackingDetailsProps {
  shipment: Shipment;
  hasMultipleShipments: boolean;
}

export const TrackingDetails = ({
  shipment,
  hasMultipleShipments,
}: TrackingDetailsProps): JSX.Element => {
  const { t } = useTranslation();
  const configs = usePTPConfigContext();
  const featureToggles = useFeatureToggleContext();
  const {
    noInfoUpdate: { isShownSearchForm, messageSearchForm },
  } = configs;
  const { enablePreprintedReturnLabel } =
    featureToggles?.returnManagement || {};

  const isReturnable = isShipmentReturnable(shipment, configs);
  const shipmentHasReturnRequests = hasReturnTickets(shipment);
  const [showReturnScreen, setShowReturnScreen] = useState(() => {
    if (enablePreprintedReturnLabel && !shipmentHasReturnRequests) {
      return false;
    }

    return isReturnable;
  });

  const renderBody = () => {
    if (isNoInfoUpdate(shipment) || shipment?.isNoInfoAvailable) {
      return (
        <>
          <ProgressIndicatorContainer>
            <ProgressIndicator shipment={shipment} />
          </ProgressIndicatorContainer>
          {isShownSearchForm && (
            <SearchFormBox>
              <RetrySearchForm messageSearchForm={messageSearchForm} />
            </SearchFormBox>
          )}
        </>
      );
    }

    if (showReturnScreen) {
      return (
        <ReturnScreen
          shipment={shipment}
          onViewTrackingDetails={() => {
            setShowReturnScreen(false);
          }}
        />
      );
    }

    /**
     * If FT is enabled and there is no return request,
     * show the button to hide tracking details, otherwise
     * hide the button to view return details
     */
    const showHideTrackingDetailsBtn =
      (!enablePreprintedReturnLabel && isReturnable && !showReturnScreen) ||
      (enablePreprintedReturnLabel && shipmentHasReturnRequests);

    return (
      <Body>
        {showHideTrackingDetailsBtn && (
          <Stack justify="center">
            <Button
              variant="link"
              onClick={() => setShowReturnScreen(true)}
              style={{ padding: 0 }}
            >
              {t('HIDE_TRACKING_DETAILS')}
            </Button>
          </Stack>
        )}
        <ProgressIndicator shipment={shipment} />
        <TrackingTimeline events={shipment.parcel.events as any} />
      </Body>
    );
  };

  return (
    <TrackingDetailsContainer>
      <TrackingDetailsHeader
        shipment={shipment}
        hasMultipleShipments={hasMultipleShipments}
      />

      {renderBody()}

      <CarrierInformation />
    </TrackingDetailsContainer>
  );
};
